import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { getApiUrl } from '../utils/apiUrl';

type Mode = 'student' | 'admin';

interface ModeContextType {
  mode: Mode;
  setMode: (mode: Mode) => void;
  isAdmin: boolean;
  canToggleMode: boolean;
}

const ModeContext = createContext<ModeContextType | undefined>(undefined);

export const useMode = () => {
  const context = useContext(ModeContext);
  if (context === undefined) {
    throw new Error('useMode must be used within a ModeProvider');
  }
  return context;
};

// Check if user is admin via API
const checkIsAdmin = async (
  userId: string | undefined,
  userEmail: string | undefined,
  getAuthToken: () => Promise<string | null>
): Promise<boolean> => {
  if (!userId) return false;

  try {
    const token = await getAuthToken();
    if (!token) return false;

    const baseUrl = getApiUrl().replace(/\/api\/?$/, '');
    const response = await fetch(`${baseUrl}/api/auth/is-admin`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      const data = await response.json();
      return data.isAdmin === true;
    }

    return false;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

export const ModeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, getAuthToken } = useAuth();
  const [mode, setModeState] = useState<Mode>('student');
  const [isAdmin, setIsAdmin] = useState(false);

  // Load mode from localStorage on mount
  useEffect(() => {
    const savedMode = localStorage.getItem('app_mode') as Mode;
    if (savedMode === 'student' || savedMode === 'admin') {
      setModeState(savedMode);
    }
  }, []);

  // Check admin status when user changes
  useEffect(() => {
    const checkAdmin = async () => {
      if (user) {
        const adminStatus = await checkIsAdmin(
          user.id,
          user.email,
          getAuthToken
        );
        setIsAdmin(adminStatus);

        // If user is not admin, force mode to student
        if (!adminStatus && mode === 'admin') {
          setModeState('student');
          localStorage.setItem('app_mode', 'student');
        }
      } else {
        setIsAdmin(false);
        setModeState('student');
      }
    };

    checkAdmin();
  }, [user, mode, getAuthToken]);

  const setMode = (newMode: Mode) => {
    // Only allow switching to admin if user is admin
    if (newMode === 'admin' && !isAdmin) {
      return;
    }
    setModeState(newMode);
    localStorage.setItem('app_mode', newMode);
  };

  const value = {
    mode,
    setMode,
    isAdmin,
    canToggleMode: isAdmin,
  };

  // Always provide the context, even during loading
  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
};
